/**
 * JWT Authentication Service for AWS Marketplace integration.
 * 
 * This service handles:
 * - Token storage in localStorage
 * - Token validation and expiration checking
 * - API calls with automatic Authorization header
 * - Subscription status fetching
 */

import { API_URL } from '../config';

const TOKEN_KEY = 'ainode_jwt_token';
const USER_KEY = 'ainode_user';

/**
 * Parse JWT payload without verification (client-side only).
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
function parseJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Failed to parse JWT:', e);
    return null;
  }
}

/**
 * Check if a JWT token is expired.
 * @param {string} token - JWT token
 * @returns {boolean} True if expired or invalid
 */
function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  
  // Add 60 second buffer for clock skew
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now + 60;
}

/**
 * Get stored JWT token if valid.
 * @returns {string|null} Token or null if not found/expired
 */
export function getToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    clearAuth();
    return null;
  }
  
  return token;
}

/**
 * Get stored user info.
 * @returns {object|null} User object or null
 */
export function getUser() {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

/**
 * Store authentication data.
 * @param {string} token - JWT token
 * @param {object} user - User information
 */
export function setAuth(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Clear authentication data.
 */
export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Check if user is authenticated.
 * @returns {boolean} True if authenticated with valid token
 */
export function isAuthenticated() {
  return getToken() !== null;
}

/**
 * Get token payload (customer_id, exp, etc.)
 * @returns {object|null} Token payload or null
 */
export function getTokenPayload() {
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
}

/**
 * Make an authenticated API request.
 * @param {string} endpoint - API endpoint (e.g., '/subscription-status')
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function authFetch(endpoint, options = {}) {
  const token = getToken();
  
  if (!token) {
    throw new Error('No authentication token available');
  }
  
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  // Handle 401 Unauthorized - clear auth and throw
  if (response.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }
  
  return response;
}

/**
 * Get subscription status from API.
 * @returns {Promise<object>} Subscription status data
 */
export async function getSubscriptionStatus() {
  const response = await authFetch('/subscription-status');
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch subscription status');
  }
  
  return response.json();
}

/**
 * Get presigned URL for file upload.
 * @param {string} contentType - MIME type of file to upload
 * @returns {Promise<object>} Presigned URL data
 */
export async function getPresignedUrl(contentType) {
  const response = await authFetch(`/upload-url?contentType=${encodeURIComponent(contentType)}`);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to get upload URL');
  }
  
  return response.json();
}

/**
 * Upload file to S3 using presigned URL.
 * @param {string} presignedUrl - S3 presigned URL
 * @param {File} file - File to upload
 * @param {string} contentType - MIME type
 * @returns {Promise<void>}
 */
export async function uploadToS3(presignedUrl, file, contentType) {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }
}

/**
 * Analyze invoice using the marketplace endpoint.
 * @param {string} s3Key - S3 key of uploaded invoice
 * @returns {Promise<object>} Analysis result
 */
export async function analyzeInvoice(s3Key) {
  const response = await authFetch('/marketplace/analyze-invoice', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ s3Key }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.error?.message || 
      error.message || 
      error.error || 
      `Error: ${response.status}`
    );
  }
  
  return response.json();
}

/**
 * Extract token from URL after Marketplace redirect.
 * @returns {string|null} Token from URL or null
 */
export function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

/**
 * Handle post-registration redirect from Marketplace.
 * Extracts token from URL, validates, stores, and cleans URL.
 * @returns {boolean} True if token was found and stored
 */
export function handleMarketplaceRedirect() {
  const token = getTokenFromUrl();
  
  if (!token) return false;
  
  // Validate token format
  if (isTokenExpired(token)) {
    console.error('Received expired token from Marketplace');
    return false;
  }
  
  // Extract user info from token
  const payload = parseJwt(token);
  const user = {
    customerId: payload.customer_id,
    email: payload.email,
    // Add more fields as needed
  };
  
  // Store auth data
  setAuth(token, user);
  
  // Clean URL (remove token parameter)
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, '', url.toString());
  
  return true;
}
